import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import SwiperClass from 'swiper';
import 'swiper/css';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';

import { CSSObject, styled } from '@mui/material';

import {
  MediaGallerySlideImage,
  MediaQuery,
  multimediaGallerySlides,
} from '@mewa/types';

import { FullScreenIcon } from '../../../assets/icons';
import { useCurrentBreakpoint } from '../../theme/breakpoints';
import VideoWithControls from '../media/video-with-controls';
import { MultimediaGalleryLightbox } from './multimedia-gallery-lightbox';
import {
  buttonDisabledClass,
  NextButton,
  PaginationContainer,
  PrevButton,
} from './swiper-elements';

type SlideConfig = {
  left: {
    image: React.ReactElement | null;
  } & MediaGallerySlideImage;
  topRight: {
    image: React.ReactElement | null;
  } & MediaGallerySlideImage;
  bottomRight: {
    image: React.ReactElement | null;
  } & MediaGallerySlideImage;
  video?: string;
};

const getActiveSlideConfig = (
  slides: {
    left: React.ReactElement | null;
    topRight: React.ReactElement | null;
    bottomRight: React.ReactElement | null;
    video?: string;
  }[],
  index: number,
  mediaQuery: MediaQuery,
): SlideConfig => ({
  left: {
    ...multimediaGallerySlides[mediaQuery][index].left,
    image: slides[index].left,
  },
  topRight: {
    ...multimediaGallerySlides[mediaQuery][index].topRight,
    image: slides[index].topRight,
  },
  bottomRight: {
    ...multimediaGallerySlides[mediaQuery][index].bottomRight,
    image: slides[index].bottomRight,
  },
  video: slides[index].video,
});

const DEFAULT_SPEED = 10000;
const DURATION = 600;

export const MultimediaGallery = ({
  slides,
  speed = DEFAULT_SPEED,
}: {
  slides: {
    left: React.ReactElement | null;
    topRight: React.ReactElement | null;
    bottomRight: React.ReactElement | null;
    video?: string;
  }[];
  speed?: number;
}) => {
  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const nextButton = useRef<HTMLButtonElement>(null);
  const prevButton = useRef<HTMLButtonElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const [activeSlideIndex, changeActiveSlide] = useState(0);
  const mq = useCurrentBreakpoint();

  const activeSlide = getActiveSlideConfig(slides, activeSlideIndex, mq);

  const getSlideIndexFromActiveLightboxIndex = useCallback(
    (activeLightboxIndex: number) => (activeLightboxIndex / 3) | 0,
    [],
  );

  const startAutoplay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      if (swiperRef) {
        swiperRef.slideNext();
      }
    }, speed);
  }, [swiperRef, speed]);

  const changeSlide = (index: number) => {
    changeActiveSlide(index);
    startAutoplay();
  };

  useEffect(() => {
    if (lightboxOpen && intervalRef.current) {
      clearInterval(intervalRef.current);
    } else if (!lightboxOpen) {
      startAutoplay();
    }
  }, [lightboxOpen, startAutoplay]);

  useEffect(() => {
    startAutoplay();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [startAutoplay]);

  useEffect(() => {
    const lightboxSlideIndex =
      getSlideIndexFromActiveLightboxIndex(activeLightboxIndex);

    if (lightboxOpen && lightboxSlideIndex !== activeSlideIndex) {
      swiperRef?.slideTo(lightboxSlideIndex);
    }
  }, [
    activeLightboxIndex,
    getSlideIndexFromActiveLightboxIndex,
    activeSlideIndex,
    swiperRef,
    lightboxOpen,
  ]);

  const lightboxSlides = useMemo(
    () =>
      slides.flatMap((slide) =>
        [
          { image: slide.left },
          { image: slide.topRight },
          { image: slide.bottomRight, video: slide.video },
        ].filter(
          (e): e is { image: React.ReactElement; video?: string } =>
            e.image !== null,
        ),
      ),
    [slides],
  );

  const handleOpenLightbox = (index: number) => {
    setActiveLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <Swiper
        modules={[Navigation, Pagination, Keyboard]}
        onSwiper={setSwiperRef}
        keyboard
        navigation={{
          nextEl: nextButton.current,
          prevEl: prevButton.current,
          disabledClass: buttonDisabledClass,
          lockClass: buttonDisabledClass,
        }}
        pagination={{
          el: paginationRef.current,
          clickable: true,
        }}
        loop
        onSlideChange={(swiper) => {
          changeSlide(swiper.realIndex);
        }}
      >
        <div style={{ position: 'relative' }}>
          <GalleryContainer>
            {activeSlide.left && (
              <LeftContainer activeSlide={activeSlide}>
                <LeftImages activeSlide={activeSlide}>
                  {slides.map((slide, index) => (
                    <ImageContainer
                      style={{ height: 'round(up, 100%, 0.1px)' }}
                      key={index}
                      isActive={index === activeSlideIndex}
                      onClick={() => handleOpenLightbox(index * 3)}
                      tabIndex={index === activeSlideIndex ? 0 : -1}
                      aria-label={'image ' + index * 3 + 1}
                    >
                      {slide.left}
                    </ImageContainer>
                  ))}
                </LeftImages>
              </LeftContainer>
            )}
            <RightContainer activeSlide={activeSlide}>
              {activeSlide.topRight && (
                <TopRightImages activeSlide={activeSlide} style={{}}>
                  {slides.map((slide, index) => (
                    <ImageContainer
                      key={index}
                      isActive={index === activeSlideIndex}
                      onClick={() => handleOpenLightbox(index * 3 + 1)}
                      tabIndex={index === activeSlideIndex ? 0 : -1}
                      aria-label={'image ' + index * 3 + 2}
                    >
                      {slide.topRight}
                    </ImageContainer>
                  ))}
                </TopRightImages>
              )}
              {activeSlide.bottomRight && (
                <BottomRightImages activeSlide={activeSlide}>
                  {slides.map((slide, index) =>
                    slide.video ? (
                      <VideoContainer
                        key={index}
                        isActive={index === activeSlideIndex}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                          }}
                        >
                          <StyledVideoWithControls
                            desktopSources={[
                              { src: slide.video, type: 'video/mp4' },
                            ]}
                            controls={false}
                            posterImage={null}
                            autoplay
                            loop
                            muted
                            showAlwaysPauseButton
                          />
                          <FullScreenButton
                            onClick={() => {
                              handleOpenLightbox(index * 3 + 2);
                            }}
                            tabIndex={index === activeSlideIndex ? 0 : -1}
                            aria-label={'video ' + index * 3 + 3}
                          >
                            <FullScreenIcon />
                          </FullScreenButton>
                        </div>
                      </VideoContainer>
                    ) : (
                      <ImageContainer
                        key={index}
                        isActive={index === activeSlideIndex}
                        style={{}}
                        onClick={() => handleOpenLightbox(index * 3 + 2)}
                        tabIndex={index === activeSlideIndex ? 0 : -1}
                        aria-label={'image ' + index * 3 + 3}
                      >
                        {slide.bottomRight}
                      </ImageContainer>
                    ),
                  )}
                </BottomRightImages>
              )}
            </RightContainer>
          </GalleryContainer>
          <PrevButton ref={prevButton} tabIndex={0} />
          <NextButton ref={nextButton} tabIndex={0} />
        </div>
        {slides.map((slide, index) => (
          <SwiperSlide key={index} />
        ))}
        <PaginationContainer ref={paginationRef} />
      </Swiper>

      <MultimediaGalleryLightbox
        slides={lightboxSlides}
        lightboxOpen={lightboxOpen}
        setLightboxOpen={setLightboxOpen}
        activeLightboxIndex={activeLightboxIndex}
        setActiveLightboxIndex={setActiveLightboxIndex}
      />
    </>
  );
};

const GalleryContainer = styled('div')(({ theme }) => ({
  '--transition-duration': `${DURATION}ms`,
  display: 'flex',
  width: '100%',
  position: 'relative',
  aspectRatio: '330 / 347',
  overflow: 'hidden',
  zIndex: 0,

  [theme.breakpoints.up('sm')]: {
    aspectRatio: '750 / 500',
  },

  [theme.breakpoints.up('md')]: {
    aspectRatio: '1290.2 / 641.25',
  },
}));

const LeftContainer = styled('div')<{ activeSlide: SlideConfig }>(
  ({ activeSlide }) => ({
    width: `${activeSlide.left.width * 100}%`,
    transition: 'width var(--transition-duration)',
    position: 'absolute',
    left: 0,
    zIndex: 2,
  }),
);

const RightContainer = styled('div')<{ activeSlide: SlideConfig }>(
  ({ activeSlide }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: `calc(max(${activeSlide.topRight.width * 100}%, ${activeSlide.bottomRight.width * 100}%) + 1px)`, // +1px to avoid margin between left and right containers based on rounding
    transition:
      'width var(--transition-duration), margin-top var(--transition-duration)',
    position: 'absolute',
    right: 0,
    zIndex: 0,
  }),
);

const LeftImages = styled('div')<{ activeSlide: SlideConfig }>(
  ({ theme, activeSlide }) => ({
    marginTop: `${activeSlide.left.marginTop * 100}%`,
    transition:
      'aspect-ratio var(--transition-duration), margin-top var(--transition-duration)',
    position: 'relative',
    aspectRatio: activeSlide.left.aspectRatio,
  }),
);

const TopRightImages = styled('div')<{ activeSlide: SlideConfig }>(
  ({ theme, activeSlide }) => ({
    marginTop: `round(down, calc(${activeSlide.topRight.marginTop * 100}% - 1px), 1px)`,
    width: `${(activeSlide.topRight.width / Math.max(activeSlide.topRight.width, activeSlide.bottomRight.width)) * 100}%`,
    aspectRatio: activeSlide.topRight.aspectRatio,
    transition:
      'aspect-ratio var(--transition-duration), width var(--transition-duration), margin var(--transition-duration)',
    position: 'relative',
    marginLeft: `${(activeSlide.topRight.marginLeft ?? 0) * 100}%`,
  }),
);

const BottomRightImages = styled('div')<{ activeSlide: SlideConfig }>(
  ({ theme, activeSlide }) => ({
    width: `${(activeSlide.bottomRight.width / Math.max(activeSlide.topRight.width, activeSlide.bottomRight.width)) * 100}%`,
    aspectRatio: activeSlide.bottomRight.aspectRatio,
    transition:
      'aspect-ratio var(--transition-duration), width var(--transition-duration)',
    position: 'relative',
  }),
);

const imageVideoStyles = (isActive: boolean): CSSObject => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  transition: 'opacity var(--transition-duration)',
  zIndex: isActive ? 1 : 0,
  opacity: isActive ? 1 : 0,
});

const ImageContainer = styled('button')<{ isActive: boolean }>(
  ({ theme, isActive }) => ({
    ...imageVideoStyles(isActive),
    border: 0,
    padding: 0,
    cursor: 'pointer',

    'img, .gatsby-image-wrapper': {
      width: '100%',
      height: '100%',
    },

    img: {
      objectFit: 'cover',
    },

    '&:focus-visible': {
      outline: '1px solid var(--color-light-red)',
      outlineOffset: '-2px',
    },
  }),
);

const VideoContainer = styled('div')<{ isActive: boolean }>(
  ({ theme, isActive }) => ({
    ...imageVideoStyles(isActive),
  }),
);

const StyledVideoWithControls = styled(VideoWithControls)(({ theme }) => ({
  width: '100%',
  height: '100%',
}));

const FullScreenButton = styled('button')(({ theme }) => ({
  padding: 0,
  border: 0,
  outline: 'none',
  background: 'transparent',
  cursor: 'pointer',
  position: 'absolute',
  top: 0,
  right: 0,

  '--size': '40px',
  width: 'var(--size)',
  height: 'var(--size)',

  [theme.breakpoints.up('sm')]: {
    '--size': '36px',
    top: '15px',
    right: '15px',
  },

  [theme.breakpoints.up('lg')]: {
    '--size': '48px',
    top: '20px',
    right: '20px',
  },

  svg: {
    color: 'var(--color-white)',
  },

  '&:hover svg, &:focus-visible svg': {
    color: 'var(--color-light-red)',
  },
}));
